import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';

import { Table } from 'primeng/table';
import { MessageService } from 'primeng/api';

import { environment } from 'src/environments/environment';

import { AgentBankBlockedCounterpartiesApiService } from 'src/app/services/agent-bank-blocked-counterparties-api.service';
import { AgentBankBlockedCounterparty } from 'src/app/shared/agent-bank-blocked-counterparties';

/**
 * Banking integration P8 follow-up: `AgentBankBlockedCounterparties` admin tab.
 * Manages the manual counterparty block list used by the transaction-staging
 * pipeline (P8/P11) for exclusion-reason `'manual_block'` matching.
 *
 * Lives in `EditUserAccessComponent` (IT/infra setup), not `HomeComponent`
 * (day-to-day operational review) — same reasoning as the bank-credentials tab.
 *
 * Mirrors `edit-agent-bank-credentials-tab.component.ts`'s shape (inline
 * row-editing table, single-edit-at-a-time via `editMode`, `uid`/`cloneIndex`
 * bookkeeping, `uid: -2` sentinel for unsaved new rows) but with a deliberate
 * difference: the `active` flag is toggled via a separate `setActive()` call,
 * never through the main `PUT` save path — see component spec for the reasoning.
 *
 * Implements filtering per the spec's requirements: global + per-column
 * client-side filters (reusing PrimeNG's `dt.filterGlobal()`/`dt.filter()`
 * API from `documents.component.html`, deliberately not `documents`'s
 * `[lazy]="true"` server-side re-querying).
 */
@Component({
  selector: 'app-edit-agent-bank-blocked-counterparties-tab',
  templateUrl: './edit-agent-bank-blocked-counterparties-tab.component.html',
  styleUrl: './edit-agent-bank-blocked-counterparties-tab.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class EditAgentBankBlockedCounterpartiesTabComponent implements OnInit, OnDestroy {

  blockedCounterparties: AgentBankBlockedCounterparty[] = [];
  blockedCounterpartiesClone: AgentBankBlockedCounterparty[] = [];

  /** Global filter input for all filterable columns. */
  blockedCounterpartiesGlobalFilter: string = '';

  /** `true` while any row is being edited — gates every other row's edit
   *  button plus "Оновити"/"Новий запис" buttons. */
  editMode: boolean = false;

  private loadQuery: Subscription = null;
  private saveQuery: Subscription = null;
  private toggleQuery: Subscription = null;

  @ViewChild('blockCounterpartiesTable') blockCounterpartiesTable: Table;

  constructor(
    private blockedCounterpartiesApi: AgentBankBlockedCounterpartiesApiService,
    private messageService: MessageService,
  ) {
  }

  ngOnDestroy(): void {
    if (this.loadQuery) { this.loadQuery.unsubscribe(); }
    if (this.saveQuery) { this.saveQuery.unsubscribe(); }
    if (this.toggleQuery) { this.toggleQuery.unsubscribe(); }
  }

  ngOnInit(): void {
    this.loadBlockedCounterparties();
  }

  loadBlockedCounterparties(): void {
    this.loadQuery =
      this.blockedCounterpartiesApi.getBlockedCounterparties().subscribe({
        next: rows => {
          this.blockedCounterparties = rows.map(r => ({ ...r, uid: r.id, cloneIndex: 0 }));
          environment.production || console.table(this.blockedCounterparties);
        },
        error: err => {
          console.error('*** EditAgentBankBlockedCounterpartiesTabComponent loadBlockedCounterparties: Error: ' + err);
          this.messageService.add(err?.status === 403
            ? { severity: 'error', summary: 'Недостаточно прав', detail: 'Потрібна роль portal-admin для керування заблокованими контрагентами' }
            : { severity: 'error', summary: 'Не вдалося завантажити список', detail: err?.message || 'Unknown error' });
        }
      });
  }

  addBlockedCounterparty(): void {
    const newRow: AgentBankBlockedCounterparty = {
      uid: -2, cloneIndex: 0, id: null,
      counterparty_iban: '', counterparty_edrpou: '', description: '', active: true
    };
    this.blockedCounterparties.push(newRow);
    this.onRowEditInit(newRow);
    this.blockCounterpartiesTable.initRowEdit(newRow);
  }

  onRowEditInit(row: AgentBankBlockedCounterparty): void {
    let newCloneIndex = 0;
    this.blockedCounterpartiesClone.forEach(r => { newCloneIndex = Math.max(newCloneIndex, r.cloneIndex || 0); });
    row.cloneIndex = ++newCloneIndex;
    this.blockedCounterpartiesClone.push({ ...row });
    this.editMode = true;
  }

  onRowEditCancel(row: AgentBankBlockedCounterparty, index: number): void {
    if (row.uid === -2) {
      this.blockedCounterparties.splice(index, 1);
    } else {
      this.blockedCounterparties[index] = { ...this.blockedCounterpartiesClone.find(r => r.cloneIndex === row.cloneIndex) };
      this.blockedCounterparties[index].cloneIndex = 0;
    }
    this.blockedCounterpartiesClone.splice(this.blockedCounterpartiesClone.findIndex(r => r.cloneIndex === row.cloneIndex), 1);
    this.editMode = false;
  }

  onRowEditSave(row: AgentBankBlockedCounterparty): void {
    // The sibling pSaveEditableRow directive on the same button already exits
    // the table's own row-edit state synchronously on this click, regardless
    // of validation or the save's outcome below -- editMode must follow that,
    // not our async result, or a validation failure or a rejected save would
    // leave every other row's pencil (and "Оновити"/"Новий запис") locked.
    this.editMode = false;

    // At least one of counterparty_iban/counterparty_edrpou must be non-empty.
    if (!(row.counterparty_iban || '').trim() && !(row.counterparty_edrpou || '').trim()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Помилка',
        detail: 'Потрібно вказати принаймні один з: IBAN або EDRPOU контрагента'
      });
      return;
    }

    const isNew = row.uid === -2;
    this.saveQuery =
      (isNew
        ? this.blockedCounterpartiesApi.createBlockedCounterparty(row)
        : this.blockedCounterpartiesApi.updateBlockedCounterparty(row)
      ).subscribe({
        next: result => {
          if (isNew && 'id' in result) { row.id = result.id; }
          row.uid = row.id;
          this.blockedCounterpartiesClone.splice(
            this.blockedCounterpartiesClone.findIndex(r => r.cloneIndex === row.cloneIndex),
            1
          );
          row.cloneIndex = 0;
        },
        error: err => {
          console.error('*** EditAgentBankBlockedCounterpartiesTabComponent onRowEditSave: Error: ' + err);
          this.messageService.add({
            severity: 'error',
            summary: 'Не вдалося зберегти',
            detail: err?.error?.message || err?.message || 'Unknown error'
          });
        }
      });
  }

  // `newState` comes from PrimeNG's `onChange` payload ($event.checked), not
  // from re-reading `row.active` here -- InputSwitch already applies the
  // toggle to the [(ngModel)]-bound `row.active` before it emits `onChange`,
  // so by the time this runs `row.active` already holds the NEW value;
  // computing `!row.active` here would silently flip it back to the OLD one
  // and send the wrong state to the server.
  toggleActive(row: AgentBankBlockedCounterparty, newState: boolean): void {
    this.toggleQuery =
      this.blockedCounterpartiesApi.setActive(row.id, newState).subscribe({
        next: () => {
          row.active = newState;
        },
        error: err => {
          console.error('*** EditAgentBankBlockedCounterpartiesTabComponent toggleActive: Error: ' + err);
          // Revert the optimistic ngModel update InputSwitch already applied,
          // since the server never persisted it.
          row.active = !newState;
          this.messageService.add({
            severity: 'error',
            summary: 'Не вдалося оновити статус',
            detail: err?.error?.message || err?.message || 'Unknown error'
          });
        }
      });
  }

  reloadTable(): void {
    this.loadBlockedCounterparties();
  }
}
